import React from 'react';
import { Check } from 'lucide-react';
import { TaskyCard } from '../../components/tasky';
import { OnboardingV2Progress } from '../../services/api/onboardingV2Service';
import { ONBOARDING_STRINGS, useMetaLang } from '../../i18n/metaReviewLang';

interface OnboardingChecklistProps {
  progress: OnboardingV2Progress;
  isInTrial: boolean;
  onNavigate: (path: string) => void;
  /** 'sidebar' (既定): 右サイドバーの小さなカード。'full': 3ステップ完了までメイン領域に表示する全面版 */
  variant?: 'sidebar' | 'full';
}

export const getAttractingQuickActionIndex = (
  progress: OnboardingV2Progress | null,
): number | null => {
  if (!progress) return null;
  if (!progress.product_done) return 0;
  if (!progress.generate_done) return 1;

  return null;
};

const STEPS = [
  {
    key: 'product' as const,
    doneKey: 'product_done' as const,
    number: 1,
    path: '/knowledge?onboarding=1',
  },
  {
    key: 'generate' as const,
    doneKey: 'generate_done' as const,
    number: 2,
    path: '/ads/banner?onboarding=1',
  },
  {
    key: 'plan' as const,
    doneKey: 'plan_done' as const,
    number: 3,
    path: '/plans',
  },
];

export const ONBOARDING_STEP_COUNT = STEPS.length;

export const getOnboardingCompletedCount = (progress: OnboardingV2Progress): number =>
  STEPS.filter((step) => progress[step.doneKey]).length;

/**
 * HomePage 側でも同一の表示条件(トライアル中 かつ completedCount < 3)を判定するための
 * 共有ヘルパー。ロジックをここに一本化し、コンポーネント側の早期 return と重複させない。
 */
export const isOnboardingChecklistVisible = (
  progress: OnboardingV2Progress | null,
  isInTrial: boolean,
): boolean => {
  if (!progress) return false;

  return isInTrial && getOnboardingCompletedCount(progress) < ONBOARDING_STEP_COUNT;
};

const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  progress,
  isInTrial,
  onNavigate,
  variant = 'sidebar',
}) => {
  const [lang] = useMetaLang();
  const t = ONBOARDING_STRINGS[lang];
  if (!isOnboardingChecklistVisible(progress, isInTrial)) {
    return null;
  }

  const completedCount = getOnboardingCompletedCount(progress);
  const isFull = variant === 'full';

  return (
    <TaskyCard
      variant="bordered"
      padding={isFull ? 'lg' : 'md'}
      className={isFull ? 'mx-auto w-full max-w-2xl bg-white' : 'bg-white'}
    >
      <div className={`flex items-center justify-between ${isFull ? 'mb-2' : 'mb-1'}`}>
        <h2 className={`font-bold text-dark ${isFull ? 'text-xl' : 'text-sm'}`}>
          {t.heading}
        </h2>
        <span className={`font-bold text-gray-400 ${isFull ? 'text-sm' : 'text-[11px]'}`}>
          {completedCount}/3
        </span>
      </div>
      <p className={`text-gray-400 ${isFull ? 'mb-5 text-sm' : 'mb-3 text-[11px]'}`}>
        {t.description}
      </p>
      <div
        className={`overflow-hidden rounded-full bg-warm-surface ${
          isFull ? 'mb-6 h-2.5' : 'mb-4 h-1.5'
        }`}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            background: 'linear-gradient(to right, #616FC9, #9675C9)',
            width: `${(completedCount / ONBOARDING_STEP_COUNT) * 100}%`,
          }}
        />
      </div>
      <div className={isFull ? 'space-y-2' : ''}>
        {STEPS.map((step, index) => {
          const done = progress[step.doneKey];

          return (
            <button
              className={`flex w-full items-center gap-3 rounded-tasky-md text-left transition ${
                isFull ? 'px-4 py-4' : 'px-2 py-3'
              } ${done ? 'cursor-default opacity-60' : 'hover:bg-warm-surface'}`}
              disabled={done}
              key={step.key}
              onClick={() => onNavigate(step.path)}
              type="button"
            >
              <span
                className={`flex flex-shrink-0 items-center justify-center rounded-full font-bold ${
                  isFull ? 'h-9 w-9 text-sm' : 'h-[26px] w-[26px] text-xs'
                } ${
                  done
                    ? 'bg-success text-white'
                    : 'border-2 border-[#D8D6CF] text-gray-400'
                }`}
              >
                {done ? (
                  <Check className={isFull ? 'h-5 w-5' : 'h-4 w-4'} strokeWidth={3} />
                ) : (
                  step.number
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block font-bold text-dark ${isFull ? 'text-base' : 'text-[13px]'}`}>
                  {t.steps[index].title}
                </span>
                <span className={`block text-gray-400 ${isFull ? 'text-sm' : 'text-[11px]'}`}>
                  {t.steps[index].description}
                </span>
              </span>
              {!done && (
                <span className={`text-right font-bold text-success ${isFull ? 'text-sm' : 'text-[11px]'}`}>
                  {t.credit(progress.bonus_credits[step.key].toLocaleString(lang === 'ja' ? 'ja-JP' : 'en-US'))}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </TaskyCard>
  );
};

export default OnboardingChecklist;
